/**
 * DEFAULT CHART TEMPLATES FOR MEDICAL SPECIALTIES
 * Each specialty has unique documentation patterns, required sections, and formatting rules
 * 
 * Ported from EMDA-Kijabe-fresh/src/data/chartTemplates/defaultSpecialtyTemplates.js
 */

const DEFAULT_SPECIALTY_TEMPLATES = {
  // COMMON SPECIALTIES
  "Internal Medicine": {
    category: "common",
    displayName: "Internal Medicine",
    description: "General internal medicine documentation with comprehensive history and physical",
    requiredSections: [
      "Chief Complaint",
      "History of Present Illness", 
      "Past Medical History",
      "Medications",
      "Allergies",
      "Social History",
      "Family History", 
      "Review of Systems",
      "Physical Examination",
      "Assessment and Plan"
    ],
    optionalSections: [
      "Vital Signs",
      "Laboratory Results",
      "Imaging Results",
      "Procedures",
      "Patient Education",
      "Follow-up Instructions"
    ],
    formatRules: {
      "Assessment and Plan": "List problems with specific plans for each",
      "Medications": "Include dose, frequency, indication",
      "Physical Examination": "Systematic head-to-toe examination"
    },
    terminology: {
      "Chief Complaint": "Brief statement in patient's own words",
      "HPI": "Chronological narrative of present illness",
      "ROS": "System-based symptom review"
    },
    promptInstructions: "Create comprehensive internal medicine documentation focusing on differential diagnosis and evidence-based management plans."
  },

  "Family Medicine": {
    category: "common", 
    displayName: "Family Medicine",
    description: "Family-centered care with focus on prevention and chronic disease management",
    requiredSections: [
      "Chief Complaint",
      "History of Present Illness",
      "Past Medical History", 
      "Medications",
      "Allergies",
      "Social History",
      "Family History",
      "Physical Examination",
      "Assessment and Plan"
    ],
    optionalSections: [
      "Preventive Care Screening",
      "Health Maintenance",
      "Patient Education", 
      "Immunization Status",
      "Behavioral Health Screening"
    ],
    formatRules: {
      "Social History": "Include occupation, exercise, diet, substance use",
      "Family History": "Focus on hereditary conditions and age of onset",
      "Preventive Care Screening": "Age-appropriate screening recommendations"
    },
    terminology: {
      "Health Maintenance": "Preventive care measures and screening schedules"
    },
    promptInstructions: "Focus on comprehensive family medicine care including prevention, health maintenance, and chronic disease management across all ages."
  },

  "Emergency Medicine": {
    category: "common",
    displayName: "Emergency Medicine", 
    description: "Acute care focused documentation with triage and disposition",
    requiredSections: [
      "Chief Complaint",
      "Triage Level", 
      "History of Present Illness",
      "Past Medical History",
      "Medications",
      "Allergies",
      "Physical Examination",
      "Emergency Department Course",
      "Diagnostic Studies",
      "Assessment and Plan",
      "Disposition"
    ],
    optionalSections: [
      "Vital Signs",
      "Pain Score",
      "Glasgow Coma Scale",
      "Procedures Performed",
      "Consultations",
      "Patient Instructions"
    ],
    formatRules: {
      "Triage Level": "ESI Level 1-5 with rationale",
      "Chief Complaint": "Brief, specific presenting complaint",
      "Disposition": "Discharge, admit, transfer with specific instructions",
      "Emergency Department Course": "Chronological timeline of ED care"
    },
    terminology: {
      "ESI": "Emergency Severity Index triage level",
      "Disposition": "Final patient placement decision"
    },
    promptInstructions: "Create emergency medicine documentation focusing on rapid assessment, differential diagnosis, and safe disposition with clear follow-up instructions."
  },

  "Pediatrics": {
    category: "common",
    displayName: "Pediatrics",
    description: "Age-appropriate pediatric documentation with growth and development",
    requiredSections: [
      "Chief Complaint",
      "History of Present Illness",
      "Birth History",
      "Past Medical History", 
      "Medications",
      "Allergies",
      "Immunization Status",
      "Developmental History",
      "Social History",
      "Family History",
      "Physical Examination",
      "Growth Parameters",
      "Assessment and Plan"
    ],
    optionalSections: [
      "Feeding History",
      "School Performance", 
      "Behavioral Assessment",
      "Safety Counseling",
      "Anticipatory Guidance"
    ],
    formatRules: {
      "Growth Parameters": "Height, weight, head circumference with percentiles",
      "Developmental History": "Age-appropriate milestones",
      "Birth History": "Gestational age, birth weight, complications",
      "Immunization Status": "Current per CDC schedule"
    },
    terminology: {
      "Growth Parameters": "Height/weight/HC percentiles for age",
      "Developmental Milestones": "Age-appropriate gross/fine motor, language, social skills"
    },
    promptInstructions: "Create pediatric documentation with focus on growth, development, and age-appropriate care including preventive measures and parent education."
  },

  "Obstetrics and Gynecology": {
    category: "common",
    displayName: "Obstetrics and Gynecology", 
    description: "Women's health documentation with obstetric and gynecologic focus",
    requiredSections: [
      "Chief Complaint",
      "History of Present Illness",
      "Obstetric History (G/P - TPAL)",
      "Gynecologic History", 
      "Past Medical History",
      "Medications",
      "Allergies",
      "Social History",
      "Physical Examination",
      "Pelvic Examination",
      "Assessment and Plan"
    ],
    optionalSections: [
      "Last Menstrual Period",
      "Contraceptive History",
      "STI Screening",
      "Pap Smear History",
      "Breast Exam",
      "Prenatal Labs"
    ],
    formatRules: {
      "Obstetric History": "G_P_ format with TPAL breakdown (Term, Preterm, Abortions, Living)",
      "Last Menstrual Period": "Date and cycle regularity",
      "Pelvic Examination": "Systematic examination of external genitalia, vagina, cervix, uterus, adnexa"
    },
    terminology: {
      "TPAL": "Term births, Preterm births, Abortions (spontaneous/induced), Living children",
      "G/P": "Gravida (total pregnancies) / Para (births after 20 weeks)"
    },
    promptInstructions: "Create OB/GYN documentation with proper obstetric notation (G/P-TPAL format), detailed gynecologic history, and comprehensive pelvic examination findings."
  },

  "Cardiology": {
    category: "common",
    displayName: "Cardiology",
    description: "Cardiovascular focused documentation with detailed cardiac assessment",
    requiredSections: [
      "Chief Complaint",
      "History of Present Illness",
      "Cardiac Risk Factors",
      "Past Medical History",
      "Medications",
      "Allergies", 
      "Family History",
      "Social History",
      "Physical Examination",
      "Cardiovascular Examination",
      "Assessment and Plan"
    ],
    optionalSections: [
      "NYHA Functional Class",
      "Chest Pain Characteristics",
      "Exercise Tolerance",
      "EKG Interpretation",
      "Echocardiogram Results",
      "Stress Test Results",
      "Cardiac Catheterization"
    ],
    formatRules: {
      "Cardiac Risk Factors": "Traditional risk factors: HTN, DM, smoking, family history, hyperlipidemia",
      "NYHA Functional Class": "Class I-IV with specific functional limitations",
      "Cardiovascular Examination": "Heart rate, rhythm, murmurs, gallops, JVD, edema",
      "Chest Pain Characteristics": "Location, quality, radiation, triggers, relieving factors"
    },
    terminology: {
      "NYHA": "New York Heart Association functional classification",
      "JVD": "Jugular venous distension",
      "S3/S4": "Third and fourth heart sounds"
    },
    promptInstructions: "Create cardiology documentation with detailed cardiovascular risk assessment, functional status (NYHA class), and comprehensive cardiac examination with interpretation of diagnostic studies."
  },

  "Surgery": {
    category: "common",
    displayName: "Surgery",
    description: "Surgical documentation with operative planning and risk assessment",
    requiredSections: [
      "Chief Complaint",
      "History of Present Illness", 
      "Past Medical History",
      "Past Surgical History",
      "Medications",
      "Allergies",
      "Social History",
      "Physical Examination",
      "Preoperative Assessment",
      "Assessment and Plan"
    ],
    optionalSections: [
      "ASA Physical Status",
      "Surgical Risk Assessment",
      "Informed Consent",
      "Preoperative Orders",
      "Postoperative Instructions",
      "Operative Planning"
    ],
    formatRules: {
      "ASA Physical Status": "ASA I-VI classification with rationale",
      "Past Surgical History": "Procedure, date, complications, surgeon",
      "Surgical Risk Assessment": "Cardiac, pulmonary, bleeding risk",
      "Operative Planning": "Procedure details, approach, anticipated complications"
    },
    terminology: {
      "ASA": "American Society of Anesthesiologists physical status classification",
      "NPO": "Nothing by mouth status"
    },
    promptInstructions: "Create surgical documentation with thorough preoperative risk assessment, ASA classification, and detailed operative planning with consideration of perioperative management."
  },

  "Psychiatry": {
    category: "common",
    displayName: "Psychiatry",
    description: "Mental health documentation with comprehensive psychiatric assessment",
    requiredSections: [
      "Chief Complaint",
      "History of Present Illness",
      "Psychiatric History",
      "Past Medical History",
      "Medications",
      "Allergies",
      "Substance Use History",
      "Social History", 
      "Family History",
      "Mental Status Examination",
      "Risk Assessment",
      "Assessment and Plan"
    ],
    optionalSections: [
      "Suicide Risk Assessment",
      "Violence Risk Assessment",
      "PHQ-9 Score",
      "GAD-7 Score",
      "Cognitive Assessment",
      "Therapeutic Alliance",
      "Treatment Compliance"
    ],
    formatRules: {
      "Mental Status Examination": "Appearance, behavior, speech, mood, affect, thought process, thought content, perceptions, cognition, insight, judgment",
      "Risk Assessment": "Suicide and violence risk with specific factors",
      "Substance Use History": "Current and past use with impact on mental health"
    },
    terminology: {
      "MSE": "Mental Status Examination",
      "SI/HI": "Suicidal ideation/Homicidal ideation",
      "PHQ-9": "Patient Health Questionnaire depression scale"
    },
    promptInstructions: "Create psychiatric documentation with comprehensive mental status examination, thorough risk assessment for suicide and violence, and evidence-based treatment planning."
  },

  "Neurology": {
    category: "common",
    displayName: "Neurology",
    description: "Neurological disorders with detailed neurologic examination",
    requiredSections: [
      "Chief Complaint",
      "History of Present Illness",
      "Neurological History",
      "Past Medical History",
      "Medications",
      "Allergies",
      "Social History",
      "Family History",
      "Neurological Examination",
      "Assessment and Plan"
    ],
    optionalSections: [
      "Mental Status Examination",
      "Cranial Nerve Examination",
      "Motor Examination",
      "Sensory Examination",
      "Cerebellar Testing",
      "Gait Assessment",
      "Imaging Results",
      "Neurophysiology Studies"
    ],
    formatRules: {
      "Neurological Examination": "Systematic assessment of mental status, cranial nerves, motor, sensory, reflexes, coordination, gait",
      "Mental Status": "Orientation, attention, memory, language, executive function",
      "Motor Examination": "Strength graded 0-5, tone, bulk, fasciculations"
    },
    terminology: {
      "NIH Stroke Scale": "Standardized neurological assessment",
      "Modified Rankin Scale": "Functional outcome measure"
    },
    promptInstructions: "Create neurology documentation with comprehensive neurological examination, appropriate use of neurological scales, and systematic approach to neurological deficits."
  },

  "Radiology": {
    category: "common",
    displayName: "Radiology",
    description: "Imaging interpretation with systematic analysis and clinical correlation",
    requiredSections: [
      "Clinical History",
      "Examination Type",
      "Technique",
      "Findings",
      "Impression",
      "Recommendations"
    ],
    optionalSections: [
      "Comparison Studies",
      "Radiation Dose",
      "Contrast Administration",
      "Limitations",
      "Follow-up Recommendations"
    ],
    formatRules: {
      "Findings": "Systematic organ-based description",
      "Impression": "Numbered list of key findings with clinical significance",
      "Technique": "Specific protocol, contrast, and technical parameters"
    },
    terminology: {
      "BIRADS": "Breast Imaging Reporting and Data System",
      "ACR": "American College of Radiology guidelines"
    },
    promptInstructions: "Create radiology documentation with systematic analysis of imaging findings, appropriate use of standardized terminology, and clinically relevant impressions with actionable recommendations."
  },

  // SUBSPECIALTIES
  "Cardiovascular Disease": {
    category: "subspecialty",
    parent: "Internal Medicine",
    displayName: "Cardiovascular Disease",
    description: "Advanced cardiovascular medicine with focus on heart disease management",
    requiredSections: [
      "Chief Complaint",
      "History of Present Illness",
      "Cardiovascular Risk Factors",
      "Past Medical History",
      "Medications",
      "Allergies",
      "Family History",
      "Social History", 
      "Physical Examination",
      "Cardiovascular Examination",
      "Diagnostic Studies Review",
      "Assessment and Plan"
    ],
    optionalSections: [
      "NYHA Functional Class",
      "CCS Angina Class",
      "Exercise Stress Test",
      "Echocardiogram",
      "Cardiac Catheterization",
      "EKG Interpretation",
      "Lipid Profile",
      "Heart Failure Assessment"
    ],
    formatRules: {
      "NYHA Functional Class": "Class I-IV with specific activity limitations",
      "CCS Angina Class": "Canadian Cardiovascular Society angina classification",
      "Cardiovascular Risk Factors": "Systematic assessment of modifiable and non-modifiable factors",
      "Diagnostic Studies Review": "Interpretation with clinical correlation"
    },
    terminology: {
      "NYHA": "New York Heart Association functional class",
      "CCS": "Canadian Cardiovascular Society angina classification",
      "LVEF": "Left ventricular ejection fraction"
    },
    promptInstructions: "Create cardiovascular subspecialty documentation with detailed functional assessment (NYHA/CCS), comprehensive risk factor analysis, and evidence-based cardiovascular disease management."
  },

  "Gastroenterology": {
    category: "subspecialty", 
    parent: "Internal Medicine",
    displayName: "Gastroenterology",
    description: "Digestive system disorders with focus on GI symptoms and procedures",
    requiredSections: [
      "Chief Complaint",
      "History of Present Illness",
      "GI History",
      "Past Medical History",
      "Medications",
      "Allergies",
      "Social History",
      "Family History",
      "Physical Examination", 
      "Abdominal Examination",
      "Assessment and Plan"
    ],
    optionalSections: [
      "Bowel Movement Pattern",
      "Diet History",
      "Alcohol Use History",
      "Hepatitis Screening",
      "Colonoscopy History",
      "Upper Endoscopy Results",
      "Liver Function Tests"
    ],
    formatRules: {
      "GI History": "Detailed symptom characterization, timing, triggers",
      "Abdominal Examination": "Systematic examination including liver span, spleen, masses",
      "Bowel Movement Pattern": "Frequency, consistency, blood, mucus"
    },
    terminology: {
      "GERD": "Gastroesophageal reflux disease",
      "IBD": "Inflammatory bowel disease",
      "IBS": "Irritable bowel syndrome"
    },
    promptInstructions: "Create gastroenterology documentation with detailed GI symptom analysis, systematic abdominal examination, and appropriate use of endoscopic and laboratory studies."
  },

  "Endocrinology, Diabetes, and Metabolism": {
    category: "subspecialty",
    parent: "Internal Medicine",
    displayName: "Endocrinology, Diabetes, and Metabolism",
    description: "Hormone disorders, diabetes management, and metabolic diseases",
    requiredSections: [
      "Chief Complaint",
      "History of Present Illness",
      "Endocrine History",
      "Past Medical History",
      "Medications",
      "Allergies",
      "Family History",
      "Social History",
      "Physical Examination",
      "Endocrine Examination",
      "Assessment and Plan"
    ],
    optionalSections: [
      "HbA1c Trends",
      "Blood Glucose Logs",
      "Thyroid Function",
      "Hormone Levels",
      "Diabetic Complications Screening",
      "Medication Adherence"
    ],
    formatRules: {
      "Endocrine History": "Detailed hormone-related symptoms and family history",
      "Diabetic Complications Screening": "Eyes, kidneys, feet, cardiovascular assessment",
      "HbA1c Trends": "Historical glycemic control patterns"
    },
    terminology: {
      "HbA1c": "Hemoglobin A1c - average blood glucose over 3 months",
      "DKA": "Diabetic ketoacidosis"
    },
    promptInstructions: "Create endocrinology documentation with detailed hormone assessment, diabetes management, and metabolic disorder evaluation."
  },

  "Infectious Disease": {
    category: "subspecialty",
    parent: "Internal Medicine",
    displayName: "Infectious Disease",
    description: "Infectious diseases management and antimicrobial stewardship",
    requiredSections: [
      "Chief Complaint",
      "History of Present Illness",
      "Infectious Disease History",
      "Past Medical History",
      "Medications",
      "Allergies",
      "Travel History",
      "Exposure History",
      "Physical Examination",
      "Assessment and Plan"
    ],
    optionalSections: [
      "Immunization History",
      "Antimicrobial History",
      "Culture Results",
      "Resistance Patterns",
      "Infection Control Measures",
      "Contact Tracing"
    ],
    formatRules: {
      "Travel History": "Recent travel with dates and geographic details",
      "Exposure History": "Animal, environmental, and human exposures",
      "Antimicrobial History": "Previous antibiotics and resistance patterns"
    },
    terminology: {
      "MRSA": "Methicillin-resistant Staphylococcus aureus",
      "C. diff": "Clostridioides difficile"
    },
    promptInstructions: "Create infectious disease documentation with detailed exposure history, antimicrobial stewardship, and infection control considerations."
  },

  "Nephrology": {
    category: "subspecialty",
    parent: "Internal Medicine",
    displayName: "Nephrology",
    description: "Kidney diseases and renal replacement therapy",
    requiredSections: [
      "Chief Complaint",
      "History of Present Illness",
      "Renal History",
      "Past Medical History",
      "Medications",
      "Allergies",
      "Family History",
      "Social History",
      "Physical Examination",
      "Assessment and Plan"
    ],
    optionalSections: [
      "Urine Analysis",
      "Creatinine Trends",
      "GFR Calculation",
      "Dialysis History",
      "Transplant Evaluation",
      "Fluid Balance"
    ],
    formatRules: {
      "Renal History": "Progression of kidney disease and previous interventions",
      "GFR Calculation": "Estimated glomerular filtration rate with staging",
      "Fluid Balance": "Daily weights and fluid restrictions"
    },
    terminology: {
      "GFR": "Glomerular filtration rate",
      "ESRD": "End-stage renal disease"
    },
    promptInstructions: "Create nephrology documentation with detailed renal function assessment, progression monitoring, and renal replacement therapy planning."
  },

  "Pulmonary Disease": {
    category: "subspecialty",
    parent: "Internal Medicine",
    displayName: "Pulmonary Disease",
    description: "Respiratory disorders and pulmonary function assessment",
    requiredSections: [
      "Chief Complaint",
      "History of Present Illness",
      "Pulmonary History",
      "Past Medical History",
      "Medications",
      "Allergies",
      "Social History",
      "Occupational History",
      "Physical Examination",
      "Pulmonary Examination",
      "Assessment and Plan"
    ],
    optionalSections: [
      "Pulmonary Function Tests",
      "Oxygen Saturation",
      "Sleep Study Results",
      "Imaging Studies",
      "Bronchoscopy Results",
      "Exercise Tolerance"
    ],
    formatRules: {
      "Pulmonary History": "Dyspnea severity, triggers, and progression",
      "Occupational History": "Exposures to dust, chemicals, or other respiratory hazards",
      "Pulmonary Function Tests": "FEV1, FVC, DLCO with interpretation"
    },
    terminology: {
      "FEV1": "Forced expiratory volume in 1 second",
      "DLCO": "Diffusion capacity of carbon monoxide"
    },
    promptInstructions: "Create pulmonary medicine documentation with detailed respiratory assessment, pulmonary function evaluation, and occupational exposure history."
  },

  "Rheumatology": {
    category: "subspecialty",
    parent: "Internal Medicine",
    displayName: "Rheumatology",
    description: "Autoimmune and inflammatory joint diseases",
    requiredSections: [
      "Chief Complaint",
      "History of Present Illness",
      "Rheumatologic History",
      "Past Medical History",
      "Medications",
      "Allergies",
      "Family History",
      "Social History",
      "Physical Examination",
      "Joint Examination",
      "Assessment and Plan"
    ],
    optionalSections: [
      "Joint Count",
      "Inflammatory Markers",
      "Autoantibody Results",
      "Imaging Studies",
      "Functional Assessment",
      "Disease Activity Scores"
    ],
    formatRules: {
      "Joint Examination": "Systematic assessment of swelling, tenderness, range of motion",
      "Joint Count": "Formal tender and swollen joint counts",
      "Disease Activity Scores": "DAS28, CDAI, or other validated measures"
    },
    terminology: {
      "DAS28": "Disease Activity Score in 28 joints",
      "Anti-CCP": "Anti-cyclic citrullinated peptide antibodies"
    },
    promptInstructions: "Create rheumatology documentation with systematic joint examination, disease activity assessment, and immunosuppressive therapy monitoring."
  },

  "Hematology and Medical Oncology": {
    category: "subspecialty",
    parent: "Internal Medicine",
    displayName: "Hematology and Medical Oncology",
    description: "Blood disorders and cancer treatment",
    requiredSections: [
      "Chief Complaint",
      "History of Present Illness",
      "Cancer History",
      "Past Medical History",
      "Medications",
      "Allergies",
      "Family History",
      "Social History",
      "Physical Examination",
      "Performance Status",
      "Assessment and Plan"
    ],
    optionalSections: [
      "Staging Information",
      "Treatment History",
      "Response Assessment",
      "Side Effects",
      "Laboratory Results",
      "Supportive Care"
    ],
    formatRules: {
      "Cancer History": "Diagnosis, staging, treatment timeline",
      "Performance Status": "ECOG or Karnofsky performance status",
      "Response Assessment": "RECIST criteria or disease-specific measures"
    },
    terminology: {
      "ECOG": "Eastern Cooperative Oncology Group performance status",
      "RECIST": "Response Evaluation Criteria In Solid Tumors"
    },
    promptInstructions: "Create hematology/oncology documentation with comprehensive cancer staging, treatment response assessment, and supportive care planning."
  },

  "Geriatric Medicine": {
    category: "subspecialty",
    parent: "Internal Medicine",
    displayName: "Geriatric Medicine",
    description: "Comprehensive care for older adults with multimorbidity",
    requiredSections: [
      "Chief Complaint",
      "History of Present Illness",
      "Past Medical History",
      "Medications",
      "Allergies",
      "Social History",
      "Functional Assessment",
      "Cognitive Assessment",
      "Physical Examination",
      "Assessment and Plan"
    ],
    optionalSections: [
      "Fall Risk Assessment",
      "Polypharmacy Review",
      "Advance Directives",
      "Caregiver Assessment",
      "Home Safety",
      "Frailty Assessment"
    ],
    formatRules: {
      "Functional Assessment": "Activities of daily living and instrumental ADLs",
      "Cognitive Assessment": "Mini-Mental State Exam or Montreal Cognitive Assessment",
      "Polypharmacy Review": "Medication reconciliation and deprescribing opportunities"
    },
    terminology: {
      "ADLs": "Activities of daily living",
      "IADLs": "Instrumental activities of daily living"
    },
    promptInstructions: "Create geriatric medicine documentation with comprehensive functional assessment, polypharmacy review, and multidisciplinary care planning."
  },

  "Pediatric Emergency Medicine": {
    category: "subspecialty",
    parent: "Emergency Medicine",
    displayName: "Pediatric Emergency Medicine",
    description: "Emergency care for pediatric patients with age-specific considerations",
    requiredSections: [
      "Chief Complaint",
      "Triage Level",
      "History of Present Illness", 
      "Birth History",
      "Past Medical History",
      "Medications",
      "Allergies",
      "Immunization Status",
      "Physical Examination",
      "Age-Appropriate Vital Signs",
      "Emergency Department Course",
      "Assessment and Plan",
      "Disposition"
    ],
    optionalSections: [
      "Growth Parameters",
      "Developmental Assessment",
      "Pain Assessment (age-appropriate)",
      "Parent/Guardian Concerns",
      "School/Daycare Information",
      "Social Concerns"
    ],
    formatRules: {
      "Age-Appropriate Vital Signs": "Age-specific normal ranges and percentiles",
      "Pain Assessment": "Age-appropriate pain scales (FLACC, Wong-Baker)",
      "Triage Level": "Pediatric-specific ESI considerations"
    },
    terminology: {
      "FLACC": "Face, Legs, Activity, Cry, Consolability pain scale",
      "PALS": "Pediatric Advanced Life Support protocols"
    },
    promptInstructions: "Create pediatric emergency medicine documentation with age-appropriate assessment tools, growth parameters, developmental considerations, and family-centered care approach."
  },

  "Pain Medicine": {
    category: "subspecialty",
    parent: "Anesthesiology",
    displayName: "Pain Medicine",
    description: "Chronic pain management and interventional procedures",
    requiredSections: [
      "Chief Complaint",
      "Pain History",
      "Past Medical History",
      "Medications",
      "Allergies",
      "Social History",
      "Physical Examination",
      "Pain Assessment",
      "Assessment and Plan"
    ],
    optionalSections: [
      "Pain Scale Assessment",
      "Functional Impact",
      "Previous Treatments",
      "Psychological Assessment",
      "Substance Use History",
      "Interventional Procedures"
    ],
    formatRules: {
      "Pain Assessment": "Location, quality, intensity, timing, aggravating/relieving factors",
      "Pain Scale Assessment": "Numeric rating scale 0-10",
      "Functional Impact": "Effect on daily activities and quality of life"
    },
    terminology: {
      "CRPS": "Complex regional pain syndrome",
      "VAS": "Visual analog scale"
    },
    promptInstructions: "Create pain medicine documentation with comprehensive pain assessment, functional evaluation, and multimodal treatment planning."
  }
};

export default DEFAULT_SPECIALTY_TEMPLATES;
